import type { ComponentType } from 'react';
import { GitHubService } from '@lved/api-gateway';
import type { GitHubAuthConfig } from '@lved/api-gateway';
import type { GitHubAuthState, LoadingSpinnerProps, ProjectLauncherProps } from '@lved/ui';
import { LVGL_PRO_SUPPORT_CONTACT_URL, ONLINE_PREVIEW_LOGOUT, VIEWER_API_ENDPOINT_URL } from '@lved/shared-core';
import type { ProjectTemplateId } from '@lved/shared-core';
import { isE2EAuthBypassBuild } from '@theia-lved-core/common/build-env';
import { ReactWidget } from '@theia/core/lib/browser';
import { Disposable } from '@theia/core/lib/common/disposable';
import { inject, injectable, postConstruct } from '@theia/core/shared/inversify';
import { WorkspaceService } from '@theia/workspace/lib/browser';
import {
    BrowserGitHubWorkspaceService,
    PrivateRepositoryLicenseRequiredError,
    type BrowserGitHubWorkspaceLoadResult
} from './github-workspace-service';

export const ONLINE_PREVIEW_HOST_ACTIVE_CHANGED_EVENT: string = 'online-preview-host-active-changed';
export const ONLINE_PREVIEW_SHOW_LAUNCHER_EVENT: string = 'online-preview-show-launcher';

const APPWRITE_CONFIG: GitHubAuthConfig = {
    endpoint: VIEWER_API_ENDPOINT_URL,
    projectId: '68796a76002e54871baf'
};

type HostState =
    | { status: 'checking-auth' }
    | { status: 'auth' }
    | { status: 'launcher' }
    | { status: 'loading'; label: string }
    | { status: 'loaded' }
    | { status: 'error'; kind: 'generic' | 'private-repository-license'; message: string };

type OnlinePreviewUiModule = Pick<
    typeof import('@lved/ui'),
    'GitHubAuth' | 'ProjectLauncher' | 'LoadingSpinner' | 'useGitHubAuthStore'
>;

@injectable()
// The `OnlinePreviewHostWidget` is the main widget for the Online Preview feature.
// It manages the authentication state, project loading, and UI rendering based
// on the current state of the application. The widget listens for events such
// as logout and show launcher, and it updates its state accordingly. It also
// handles loading projects from GitHub and displays appropriate UI components
// based on the current state (e.g., loading spinner, error message).
export class OnlinePreviewHostWidget extends ReactWidget {
    static readonly ID: string = 'widget:theia-browser-host';

    @inject(BrowserGitHubWorkspaceService)
    protected readonly githubWorkspaceService!: BrowserGitHubWorkspaceService;

    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    protected uiModule: OnlinePreviewUiModule | undefined;
    protected hostState: HostState = { status: 'checking-auth' };
    protected unsubscribeAuthStore?: () => void;
    protected attemptedInitialRepoLoad: boolean = false;

    @postConstruct()
    protected init(): void {
        this.id = OnlinePreviewHostWidget.ID;
        this.title.closable = false;
        this.node.id = 'lvgl-online-preview';
        this.toDispose.push(
            Disposable.create(() => {
                this.unsubscribeAuthStore?.();
                this.unsubscribeAuthStore = undefined;
            })
        );
        this.toDispose.push(
            this.workspaceService.onWorkspaceChanged(() => {
                this.syncFromAuthState();
                this.update();
            })
        );
        this.toDispose.push(
            Disposable.create(() => {
                window.removeEventListener(ONLINE_PREVIEW_LOGOUT, this.handleLogout);
                window.removeEventListener(ONLINE_PREVIEW_SHOW_LAUNCHER_EVENT, this.handleShowLauncher);
            })
        );
        window.addEventListener(ONLINE_PREVIEW_LOGOUT, this.handleLogout);
        window.addEventListener(ONLINE_PREVIEW_SHOW_LAUNCHER_EVENT, this.handleShowLauncher);
        void this.loadModule();
    }

    protected async loadModule(): Promise<void> {
        try {
            this.uiModule = (await import('@lved/ui')) as unknown as OnlinePreviewUiModule;
            this.bindAuthState();
            // Skip the AppWrite auth check only for E2E bundles built with the auth bypass flag.
            if (!isE2EAuthBypassBuild()) {
                await this.uiModule.useGitHubAuthStore.getState().initialize(APPWRITE_CONFIG);
            }
            this.syncFromAuthState();
        } catch (error: unknown) {
            this.hostState = {
                status: 'error',
                kind: 'generic',
                message: error instanceof Error ? error.message : 'Failed to load Online Preview.'
            };
            this.syncActiveState();
        }
        this.update();
    }

    // Subscribes to the Zustand auth store so that any future auth state change
    // automatically re-derives hostState. Cancels the previous subscription first
    // to avoid duplicate listeners if called more than once.
    protected bindAuthState(): void {
        if (!this.uiModule) return;
        this.unsubscribeAuthStore?.();
        this.unsubscribeAuthStore = this.uiModule.useGitHubAuthStore.subscribe(() => {
            this.syncFromAuthState();
            this.update();
        });
    }

    // Reads the current auth store snapshot and maps it to the correct hostState.
    // Order matters: checking-auth and unauthenticated are handled first, then
    // already-open workspaces, then in-flight/error states (left untouched to avoid
    // overwriting them), and finally the one-shot auto-load from the URL repo param
    // before falling back to the launcher.
    protected syncFromAuthState(): void {
        if (!this.uiModule) return;

        const authState: GitHubAuthState = this.uiModule.useGitHubAuthStore.getState();
        if (!authState.hasCheckedAuth && !isE2EAuthBypassBuild()) {
            this.hostState = { status: 'checking-auth' };
            this.syncActiveState();
            return;
        }

        if (this.githubWorkspaceService.isOnlinePreviewWorkspaceOpen()) {
            if (!this.attemptedInitialRepoLoad) {
                this.attemptedInitialRepoLoad = true;
                void this.finishRestoredWorkspaceLoad();
                return;
            }
            if (this.hostState.status === 'loading' || this.hostState.status === 'error') {
                this.syncActiveState();
                return;
            }
            this.hostState = { status: 'loaded' };
            this.syncActiveState();
            return;
        }

        // Keep in-flight load / error visible; still sync overlay active state.
        if (this.hostState.status === 'loading' || this.hostState.status === 'error') {
            this.syncActiveState();
            return;
        }

        const repoParam: string | null = GitHubService.getRawRepoParam();
        if (repoParam && !this.attemptedInitialRepoLoad) {
            if (!authState.isAuthenticated && !isE2EAuthBypassBuild()) {
                this.hostState = { status: 'auth' };
                this.syncActiveState();
                return;
            }
            this.attemptedInitialRepoLoad = true;
            void this.loadAuthenticatedProject(repoParam);
            return;
        }

        this.hostState = { status: 'launcher' };
        this.syncActiveState();
    }

    // Hash restore: rehydrate stores, then sync remote tip.
    protected async finishRestoredWorkspaceLoad(): Promise<void> {
        this.hostState = { status: 'loading', label: 'Loading project...' };
        this.syncActiveState();
        this.update();

        try {
            await this.githubWorkspaceService.syncUiStoresForOpenWorkspace();
            this.hostState = { status: 'loaded' };
            this.syncActiveState();
            this.update();

            if (this.githubWorkspaceService.isGitHubOnlinePreviewWorkspaceOpen() && GitHubService.getRawRepoParam()) {
                void this.githubWorkspaceService.syncFromGitHub();
            }
        } catch (error: unknown) {
            this.hostState = {
                status: 'error',
                kind: error instanceof PrivateRepositoryLicenseRequiredError ? 'private-repository-license' : 'generic',
                message: error instanceof Error ? error.message : String(error)
            };
            this.syncActiveState();
            this.update();
        }
    }

    protected readonly loadProject = async (repoUrl: string): Promise<void> => {
        this.setRepoSearchParam(repoUrl);
        const isAuthenticated: boolean =
            this.uiModule?.useGitHubAuthStore.getState().isAuthenticated === true || isE2EAuthBypassBuild();
        if (!isAuthenticated) {
            this.attemptedInitialRepoLoad = false;
            this.hostState = { status: 'auth' };
            this.syncActiveState();
            this.update();
            return;
        }

        this.attemptedInitialRepoLoad = true;
        await this.loadAuthenticatedProject(repoUrl);
    };

    protected async loadAuthenticatedProject(repoUrl: string): Promise<void> {
        this.hostState = { status: 'loading', label: 'Loading project from GitHub...' };
        this.syncActiveState();
        this.update();

        try {
            const result: BrowserGitHubWorkspaceLoadResult = await this.githubWorkspaceService.loadRepository(repoUrl);
            console.debug(
                `[Online Preview] Loaded ${result.fileCount} file(s) into browser workspace ${result.workspacePath}.`
            );
            this.hostState = { status: 'loaded' };
        } catch (error: unknown) {
            this.hostState = {
                status: 'error',
                kind: error instanceof PrivateRepositoryLicenseRequiredError ? 'private-repository-license' : 'generic',
                message: error instanceof Error ? error.message : String(error)
            };
        }

        this.syncActiveState();
        this.update();
    }

    protected readonly loadTemplate = async (templateId: ProjectTemplateId): Promise<void> => {
        this.clearRepoSearchParam();
        this.hostState = { status: 'loading', label: `Loading bundled ${templateId}...` };
        this.syncActiveState();
        this.update();

        try {
            const result: BrowserGitHubWorkspaceLoadResult =
                await this.githubWorkspaceService.loadBundledTemplate(templateId);
            console.debug(
                `[Online Preview] Loaded ${result.fileCount} bundled file(s) into browser workspace ${result.workspacePath}.`
            );
            this.hostState = { status: 'loaded' };
        } catch (error: unknown) {
            this.hostState = {
                status: 'error',
                kind: 'generic',
                message: error instanceof Error ? error.message : String(error)
            };
        }

        this.syncActiveState();
        this.update();
    };

    protected readonly handleLogout = (): void => {
        void this.logout();
    };

    protected readonly handleShowLauncher = (): void => {
        void this.showLauncher();
    };

    protected async logout(): Promise<void> {
        this.hostState = { status: 'loading', label: 'Signing out...' };
        this.syncActiveState();
        this.update();

        const didLogout: boolean = await this.githubWorkspaceService.logout();
        if (!didLogout) {
            this.syncFromAuthState();
            this.update();
            return;
        }

        this.attemptedInitialRepoLoad = false;
        this.hostState = { status: 'auth' };
        this.syncActiveState();
        this.update();
    }

    protected setRepoSearchParam(repoUrl: string): void {
        const url: URL = new URL(window.location.href);
        url.searchParams.set('repo', repoUrl);
        window.history.pushState({}, document.title, url.toString());
    }

    protected clearRepoSearchParam(): void {
        this.githubWorkspaceService.clearOnlinePreviewLocation();
    }

    protected async showLauncher(): Promise<void> {
        const canCloseWorkspace: boolean = await this.githubWorkspaceService.closeWorkspaceForLauncher();
        if (!canCloseWorkspace) return;

        this.clearRepoSearchParam();
        this.hostState = { status: 'launcher' };
        this.syncActiveState();
        this.update();
    }

    // "Active" means the overlay is visible — any state other than 'loaded' blocks
    // the editor view. Toggles the CSS class on this widget's node and fires a
    // window event so external listeners (e.g. shell, toolbar) can react.
    protected syncActiveState(): void {
        const isActive: boolean = this.hostState.status !== 'loaded';
        this.node.classList.toggle('lvgl-online-preview-active', isActive);
        window.dispatchEvent(
            new CustomEvent(ONLINE_PREVIEW_HOST_ACTIVE_CHANGED_EVENT, {
                detail: { active: isActive }
            })
        );
    }

    protected override render(): JSX.Element {
        if (this.hostState.status === 'error') {
            return this.renderError(this.hostState);
        }

        if (!this.uiModule || this.hostState.status === 'checking-auth') {
            return this.renderLoading('Checking GitHub session...');
        }

        if (this.hostState.status === 'auth') {
            const GitHubAuth: ComponentType = this.uiModule.GitHubAuth;
            return (
                <div className="lvgl-online-preview-screen">
                    <GitHubAuth />
                </div>
            );
        }

        if (this.hostState.status === 'launcher') {
            const ProjectLauncher: ComponentType<ProjectLauncherProps> = this.uiModule.ProjectLauncher;
            return (
                <div className="lvgl-online-preview-screen">
                    <ProjectLauncher onLoadProject={this.loadProject} onLoadTemplate={this.loadTemplate} />
                </div>
            );
        }

        if (this.hostState.status === 'loading') {
            return this.renderLoading(this.hostState.label);
        }

        return <></>;
    }

    protected renderLoading(label: string): JSX.Element {
        const LoadingSpinner: ComponentType<LoadingSpinnerProps> | undefined = this.uiModule?.LoadingSpinner;
        return (
            <div className="lvgl-online-preview-screen">
                <div className="lvgl-online-preview-loading">
                    {LoadingSpinner ? <LoadingSpinner size={28} thickness={3} label={label} /> : label}
                </div>
            </div>
        );
    }

    protected renderError(state: Extract<HostState, { status: 'error' }>): JSX.Element {
        const isPrivateRepositoryLicenseError: boolean = state.kind === 'private-repository-license';
        return (
            <div className="lvgl-online-preview-screen">
                <div className="lvgl-online-preview-error" role="alert">
                    <h1>Project could not be loaded</h1>
                    <p>{state.message}</p>
                    <div className="lvgl-online-preview-error-actions">
                        {isPrivateRepositoryLicenseError && (
                            <a
                                className="lvgl-online-preview-error-primary-action"
                                href={LVGL_PRO_SUPPORT_CONTACT_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Contact Us
                            </a>
                        )}
                        <button
                            className={
                                isPrivateRepositoryLicenseError
                                    ? 'lvgl-online-preview-error-secondary-action'
                                    : 'lvgl-online-preview-error-primary-action'
                            }
                            type="button"
                            onClick={() => void this.showLauncher()}
                        >
                            Back to Launcher
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
